import { BoundingBox, GeoDetails, GeoTag, Location } from "../services/types";

// Store our overlapping county fips codes for BLS and TRS data queries
export var countyId: string[] = [];
export const setCountyIds = (id: string[]) => {
    countyId = id;
}
export const getCountyIds = () => {
    return countyId;
}

// Store our bounding box so we can access it later
export let boundingBox: BoundingBox | null = null;
export const setBoundingBox = (bb: BoundingBox) => {
    boundingBox = bb;
}

// Store our excludeIds so we can access them later
export let excludeIds: string[] | null = null;
export const setExcludeIds = (ids: string[]) => {
    excludeIds = ids;
}


export const fetchMetricCategories = async (orgId: number) => {
    if (!orgId) {
        console.log('No orgId provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_STORYMAP_BACKEND_URL}/api/getmetriccategories?orgId=${orgId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

export const fetchMetricMetadata = async (id: number) => {
    if (!id) {
        console.log('No id provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_STORYMAP_BACKEND_URL}/api/getmetricmetadata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

export const fetchMetricMetadataByOrg = async (orgId: number) => {
    if (!orgId) {
        console.log('No orgId provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_STORYMAP_BACKEND_URL}/api/getmetadatabyorg`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orgId }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

// fetch PercentileDist - get percentile distribution information by org and category
export const fetchPercentileDist = async (orgId: number, category: string) => {
    if (!orgId || !category) {
        console.log('No orgId or category provided to fetch metric percentile distributions');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_STORYMAP_BACKEND_URL}/api/getpercentiledist?orgId=${orgId}&category=${category}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

// fetchDistributionData - get distribution data by metric id and selected area
export const fetchDistributionData = async (metricId: number, geoDetails: GeoDetails[], geoTag: GeoTag | null) => {
    if (!metricId) {
        console.log('No metricId provided to fetch indicator distribution data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_STORYMAP_BACKEND_URL}/api/getdistributiondata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, geoDetails, geoTag, geoEncoding: 'delta', allowDefault: true }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching indicator distribution data:', error);
    }
};

// fetchIndicatorBinCount - get indicator data and bin count by metric id and selected area
export const fetchIndicatorBinCount = async (metricId: number, geoDetails: GeoDetails[], geoTag: GeoTag | null) => {
    if (!metricId) {
        console.log('No metricId provided to fetch indicator distribution data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_STORYMAP_BACKEND_URL}/api/getindicatorbincounts`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, geoDetails, geoTag, geoEncoding: 'delta', allowDefault: true }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching indicator distribution data:', error);
    }
};

// fetchStateData - retreive state data for a selected metric using a prefix state code
export const fetchStateData = async (metricId: number, geoPrefix: string) => {
    if (!metricId || !geoPrefix) {
        console.log('No metricId or geoDetails provided to fetch metric state data');
        return;
    }

    console.log('geoprefix:', geoPrefix);

    try {
        const response = await fetch(`${import.meta.env.VITE_STORYMAP_BACKEND_URL}/api/getgeoprefixdata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, geoPrefix }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric state data:', error);
    }
};

// fetchNationalData - retreive national data for a selected metric
export const fetchNationalData = async (metricId: number) => {
    if (!metricId) {
        console.log('No metricId provided to fetch national metric data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_STORYMAP_BACKEND_URL}/api/getgeoprefixdata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching national metric data:', error);
    }
};

export const calculateGeoPrefix = (geoDetails: GeoDetails[]): string => {
    // Flatten the geoIds array
    const geoIds = geoDetails.flatMap(geo => geo.ids);

    // Helper function to get the first two digits of a geoId
    const getGeoPrefix = (geoId: string): string => geoId.substring(0, 2);

    // Count occurrences of each prefix
    const prefixCount: Record<string, number> = {};
    geoIds.forEach(geoId => {
        const prefix = getGeoPrefix(geoId);
        prefixCount[prefix] = (prefixCount[prefix] || 0) + 1;
    });

    // Find the prefix with the highest count
    return Object.entries(prefixCount).reduce((prev, curr) => 
        curr[1] > prev[1] ? curr : prev, ['', 0]
    )[0];
};

export const fetchCountyFips = async (orgId: number | null): Promise<GeoTag | null> => {
    try {
        const response = await fetch(import.meta.env.LOCATIONS_API_URL);
        const locations: any[] = await response.json();
        const matchingLocation = locations.find(location => Number(location.orgId) === orgId);
        
        return matchingLocation 
        ? { type: "county", value: matchingLocation.countyFips } 
        : null;
    } catch (error) {
        console.error("Error fetching county FIPS:", error);
        return null;
    }
};

export const fetchAvailableLayers = async (locationId: string | null): Promise<string | null> => {
    console.log('location id passed to fetchAvailableLayers:', locationId);
    console.log('retool endpoint:', import.meta.env.VITE_LOCATIONS_API_URL);

    try {
        const response = await fetch(import.meta.env.VITE_LOCATIONS_API_URL);
        const locations: Location[] = await response.json();
        const matchingLocation = locations.find(location => location.locationId === locationId);

        console.log('locations:', locations);
        console.log('matching location:', matchingLocation);
        
        return matchingLocation 
        ? matchingLocation.programmaticLayers 
        : null;
    } catch (error) {
        console.error("Error fetching county FIPS:", error);
        return null;
    }
};
